import type { QueryClient } from '@tanstack/react-query'

import type {
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import {
  api,
  AUTH_ACCESS_TOKEN_STORAGE_KEY,
  clearRefreshTimer,
  handleAxiosError,
  scheduleProactiveRefresh
} from '@/lib/axios'

import type {
  PasswordOtpSentResponse,
  PasswordOtpVerifyResponse,
  PasswordResetResponse,
  SignInRequestValue,
  SSOCallbackRequestValue,
  SSORedirectResponse,
  TProfileResponse,
  TRefreshResponse,
  TSignInResponse
} from './types'

export const AUTH_QUERY_KEY = (key?: string | Record<string, any>) =>
  ['profile', key] as const

const PROFILE_STORAGE_KEY = '__app-profile-data'

const sanitizeProfile = (profile: TProfileResponse): TProfileResponse => ({
  ...profile,
  modules: (profile.modules ?? []).filter(Boolean)
})

const getProfile = (): ServiceQueryOptionType<TProfileResponse | null> => ({
  queryKey: AUTH_QUERY_KEY(),
  queryFn: async () => {
    const token = localStorage.getItem(AUTH_ACCESS_TOKEN_STORAGE_KEY)
    if (!token) return null

    try {
      const response =
        await api.get<ServiceGetByIdResponse<TProfileResponse>>('/auth/profile')

      if (!response.data.data) {
        throw new Error('Profile not found')
      }
      const profile = sanitizeProfile(response.data.data)

      // Simpan profile ke localStorage untuk offline access
      localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profile))

      return profile
    } catch (e) {
      const error = handleAxiosError(e)

      if (error.status === 401 || error.status === 403) {
        localStorage.removeItem(AUTH_ACCESS_TOKEN_STORAGE_KEY)
        localStorage.removeItem(PROFILE_STORAGE_KEY)
        return null
      }

      // Jika network error atau offline, coba ambil dari localStorage
      // Service worker akan menangani caching, tapi ini sebagai fallback
      const cachedProfile = localStorage.getItem(PROFILE_STORAGE_KEY)

      if (token && cachedProfile) {
        try {
          return sanitizeProfile(JSON.parse(cachedProfile) as TProfileResponse)
        } catch {
          // Jika parsing gagal, hapus cache yang corrupt
          localStorage.removeItem(PROFILE_STORAGE_KEY)
        }
      }

      return null
    }
  },
  // Menggunakan staleTime yang lebih lama untuk offline support
  staleTime: 5 * 60 * 1000, // 5 menit
  // Retry dengan exponential backoff untuk network errors
  retry: (failureCount, error: any) => {
    // Jangan retry jika 401 (unauthorized) atau 403 (forbidden)
    if (error?.status === 401 || error?.status === 403) return false
    // Retry maksimal 3 kali untuk network errors
    return failureCount < 3
  },
  retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
})

const signIn = (
  queryClient: QueryClient
): ServiceMutationOptionType<TSignInResponse, SignInRequestValue> => ({
  mutationFn: async reqBody => {
    try {
      const response = await api.post<TSignInResponse>('/auth/login', reqBody)
      const { access_token } = response.data.data

      localStorage.setItem(AUTH_ACCESS_TOKEN_STORAGE_KEY, access_token)
      scheduleProactiveRefresh(access_token)

      queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY() })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const signOut = (
  queryClient: QueryClient
): ServiceMutationOptionType<unknown, void> => ({
  mutationFn: async () => {
    try {
      await api.post('/auth/logout', {})

      clearRefreshTimer()
      localStorage.removeItem(AUTH_ACCESS_TOKEN_STORAGE_KEY)
      localStorage.removeItem(PROFILE_STORAGE_KEY)

      queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY() })
    } catch (error) {
      // Bahkan jika logout gagal di server, tetap hapus data lokal
      clearRefreshTimer()
      localStorage.removeItem(AUTH_ACCESS_TOKEN_STORAGE_KEY)
      localStorage.removeItem(PROFILE_STORAGE_KEY)
      queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY() })

      throw handleAxiosError(error)
    }
  }
})

const requestPasswordOtp = (): ServiceMutationOptionType<
  PasswordOtpSentResponse,
  { username: string }
> => ({
  retry: false,
  mutationFn: async ({ username }) => {
    try {
      const response = await api.post<PasswordOtpSentResponse>(
        '/auth/password/otp',
        { username }
      )

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const verifyPasswordOtp = (): ServiceMutationOptionType<
  PasswordOtpVerifyResponse,
  { username: string; otp: string }
> => ({
  retry: false,
  mutationFn: async ({ username, otp }) => {
    try {
      const response = await api.post<PasswordOtpVerifyResponse>(
        '/auth/password/otp/verify',
        { username, otp }
      )

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const refreshToken = async (): Promise<string | null> => {
  const token = localStorage.getItem(AUTH_ACCESS_TOKEN_STORAGE_KEY)
  if (!token) return null
  try {
    const response = await api.post<TRefreshResponse>('/auth/refresh')
    const { access_token, user } = response.data.data
    localStorage.setItem(AUTH_ACCESS_TOKEN_STORAGE_KEY, access_token)
    scheduleProactiveRefresh(access_token)
    if (user) {
      localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(user))
    }
    return access_token
  } catch {
    return null
  }
}

const getSSORedirectURL = async (state: string): Promise<string> => {
  try {
    const response = await api.get<SSORedirectResponse>(
      '/auth/oauth/simluhtan/redirect',
      { params: { state } }
    )
    return response.data.redirect_url
  } catch (error) {
    throw handleAxiosError(error)
  }
}

const ssoCallback = (
  queryClient: QueryClient
): ServiceMutationOptionType<TSignInResponse, SSOCallbackRequestValue> => ({
  mutationFn: async reqBody => {
    try {
      const response = await api.post<TSignInResponse>(
        '/auth/oauth/simluhtan/callback',
        reqBody
      )
      const { access_token } = response.data.data

      localStorage.setItem(AUTH_ACCESS_TOKEN_STORAGE_KEY, access_token)
      scheduleProactiveRefresh(access_token)

      queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY() })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const resetPassword = (): ServiceMutationOptionType<
  PasswordResetResponse,
  {
    reset_token: string
    password: string
    password_confirmation: string
  }
> => ({
  retry: false,
  mutationFn: async data => {
    try {
      const response = await api.post<PasswordResetResponse>(
        '/auth/password/reset',
        data
      )
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const auth = {
  getProfile,
  signIn,
  signOut,
  refreshToken,
  requestPasswordOtp,
  verifyPasswordOtp,
  resetPassword,
  getSSORedirectURL,
  ssoCallback
}
